import React, { useEffect, useId, useRef, useState } from 'react';

const Chevron = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);

// A filter dropdown with a checkbox per option and a search box once the list
// is long enough to need one. Nothing selected means everything, which is how
// the button reads too ("All companies").
const MultiSelect = ({ label, allLabel, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]);

  const needle = query.trim().toLowerCase();
  const shown = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? options.find((o) => o.id === selected[0])?.label || `1 ${label.toLowerCase()}`
        : `${selected.length} ${label.toLowerCase()}`;
  const active = selected.length > 0;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        className={`flex items-center gap-2 max-w-[16rem] px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 border ${
          active
            ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 border-purple-400/40'
            : 'bg-gray-800/60 hover:bg-gray-700/60 text-gray-300 border-gray-700 hover:border-gray-500'
        }`}
      >
        <span className="truncate">{summary}</span>
        <Chevron />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] bg-gray-800 border border-purple-500/20 rounded-lg shadow-2xl z-40">
          {options.length > 6 && (
            <div className="p-2 border-b border-gray-700">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}`}
                aria-label={`Search ${label.toLowerCase()}`}
                className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-md text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}
          <ul id={listId} role="listbox" aria-multiselectable="true" aria-label={label} className="max-h-64 overflow-y-auto py-1">
            {shown.map((option) => {
              const checked = selected.includes(option.id);
              return (
                <li key={option.id} role="option" aria-selected={checked}>
                  <label className="flex items-center gap-3 px-3 py-2 text-sm text-gray-200 hover:bg-purple-500/10 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(option.id)}
                      className="w-4 h-4 accent-purple-500 flex-shrink-0"
                    />
                    <span className="truncate flex-1">{option.label}</span>
                    {option.meta && <span className="text-xs text-gray-500 flex-shrink-0">{option.meta}</span>}
                  </label>
                </li>
              );
            })}
            {shown.length === 0 && <li className="px-3 py-3 text-sm text-gray-500">No matches.</li>}
          </ul>
          {active && (
            <div className="p-2 border-t border-gray-700 flex justify-end">
              <button
                type="button"
                onClick={() => onChange([])}
                className="bg-transparent hover:bg-gray-700/60 text-gray-300 hover:text-white px-3 py-1.5 rounded-md text-xs font-medium"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
